import mapValues from 'lodash/mapValues';
import React from 'react';
import { getNewPlatformIcon } from '@/widgets/components/online-platform-icon';
import styled from 'styled-components';
import {
  IconSizer,
  IssueFreeMessage,
  IssueReportWrapper,
  IssueSummaryReport,
  IssueSummaryTitle,
  StyledText,
  StyledViolationTitle,
} from './common-report-components';

const TagWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-right: 20px;
  margin-bottom: 10px;
  /* justify-content: center; */
  align-items: center;
`;

const TagGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 10px;
`;

/**
 * Represents the icon + id tag of the violator
 * @param {id, platform} id: Listing id, platform: online platform
 */
function PlatformViolationTag(props) {
  const { id, platform } = props;

  const PlatformIcon = getNewPlatformIcon(platform);

  return (
    <TagWrapper>
      <IconSizer>
        <PlatformIcon className="platform-icon" />
      </IconSizer>
      <StyledText>{id}</StyledText>
    </TagWrapper>
  );
}

/**
 * creates styled title and tag group for all violators
 * @param {title, violationListings} Data for each violation group
 */
function STRViolation(props) {
  const { title, violationListings } = props;
  return (
    <>
      <StyledViolationTitle title={title} />
      <TagGroup>
        {violationListings.map((listing, i) => (
          <PlatformViolationTag
            key={i}
            id={listing.id}
            platform={listing.platform}
          />
        ))}
      </TagGroup>
    </>
  );
}

/**
 * Extract the violation information and build tags to display in report
 * @param {listings} online platform listings for each property
 */
function groupListingsByViolationType(listings) {
  const violationData = {};
  listings?.forEach((listing) => {
    const { violations } = listing;
    const listingTagInfo = { id: listing.id, platform: listing.platform };
    violations.forEach((violation) => {
      if (violationData[violation]) {
        violationData[violation].push(listingTagInfo);
      } else {
        violationData[violation] = [listingTagInfo];
      }
    });
  });
  return violationData;
}

/**
 * Counts number of violations for each type among the given listings
 * @param listings Listings that may contain violations
 * @returns {Map<string, number>} A map of violation type to number of violations of each type
 * encountered among the `listings`
 */
function countListingViolationsByType(listings) {
  return mapValues(
    groupListingsByViolationType(listings),
    (violationListings) => violationListings.length
  );
}

const violationDetailsReportTitle = 'Possible Violations';
const violationDetailsReportNoViolationsMessage =
  'We found no violations for this property';
/**
 * Component to return the report of violations for each property. If No Violations
 * the component renders the message no "We found no violation for all listing for this property."
 * And the color of the wrapper changes to green. If the optional parameter bordered is passed
 * then it returns with no wrapper at all.
 * @returns Violation Report either with or without wrapper
 */
export function ViolationDetailsReport({ listings = [], bordered }) {
  const formattedData = groupListingsByViolationType(listings);
  const violationTypes = Object.keys(formattedData);
  const WrapperComponent = bordered ? 'div' : IssueReportWrapper;

  return (
    <WrapperComponent free={+!violationTypes.length}>
      <IssueSummaryTitle>{violationDetailsReportTitle}</IssueSummaryTitle>
      {violationTypes.length ? (
        violationTypes.map((type) => (
          <STRViolation
            key={type}
            title={type}
            violationListings={formattedData[type]}
          />
        ))
      ) : (
        <IssueFreeMessage>
          {violationDetailsReportNoViolationsMessage}
        </IssueFreeMessage>
      )}
    </WrapperComponent>
  );
}

/**
 * Component to display a summarized view of violations. Unlike `ViolationDetailsReport`,
 * this report displays only the number of violation by type (e.g. "Excess Occupancy (3)").
 * Only the top 2 violation types by count are displayed.
 * @param props.listings Listings that may contain violations to be displayed
 * @param {string} props.css CSS styles to apply to the report wrapper element.
 * See usage of the css prop from `styled-components` for more information.
 * @param {string} props.className CSS classes to apply to the report wrapper element.
 * Allows report to be styleable via styled-components.
 * @returns {JSX.Element}
 * @constructor
 */
export function ViolationSummaryReport(props) {
  const { className, css, onClick, listings = [] } = props;
  return (
    <IssueSummaryReport
      className={className}
      css={css}
      onClick={onClick}
      title={violationDetailsReportTitle}
      noIssuesMessage={violationDetailsReportNoViolationsMessage}
      issueCountByType={countListingViolationsByType(listings)}
    />
  );
}
